import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useState } from "react";
import { MatchCard } from "@/components/matching/MatchCard";
import { MatchModal } from "@/components/matching/MatchModal";
import { AppHeader } from "@/components/layout/AppHeader";
import { AIChatPanel } from "@/components/ai-chat";
import { Loader2, Heart } from "lucide-react";
import { useProfileCheck } from "@/hooks/useProfileCheck";

interface Match {
  userId: string;
  profileId: string; // Profile ID for AI chat context
  profile: {
    displayName: string;
    age: number;
    photos: string[];
    bio: string;
    lookingFor: string;
  };
  compatibilityScore: number;
  explanation: {
    summary: string;
    reasons: string[];
  };
}

interface MatchFeedResponse {
  matches: Match[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export function Feed() {
  const { getToken } = useAuth();
  const { isChecking } = useProfileCheck(); // This will redirect if profile incomplete
  const [currentIndex, setCurrentIndex] = useState(0);
  const [matchedUser, setMatchedUser] = useState<Match | null>(null);

  const { data, isLoading, error } = useQuery<MatchFeedResponse>({
    queryKey: ["matchFeed"],
    queryFn: async () => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiUrl}/api/matches/feed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch matches");
      return res.json();
    },
  });

  const likeMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiUrl}/api/matches/like`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ targetUserId }),
      });
      if (!res.ok) throw new Error("Failed to like");
      return res.json();
    },
    onSuccess: (result, targetUserId) => {
      if (result.matched) {
        const match = data?.matches.find((m) => m.userId === targetUserId);
        if (match) setMatchedUser(match);
      }
      setCurrentIndex((i) => i + 1);
    },
  });

  const passMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${apiUrl}/api/matches/pass`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ targetUserId }),
      });
      if (!res.ok) throw new Error("Failed to pass");
      return res.json();
    },
    onSuccess: () => setCurrentIndex((i) => i + 1),
  });

  const handleLike = () => {
    const match = data?.matches[currentIndex];
    if (match) likeMutation.mutate(match.userId);
  };

  const handlePass = () => {
    const match = data?.matches[currentIndex];
    if (match) passMutation.mutate(match.userId);
  };

  const handleSwipe = (direction: "left" | "right") => {
    if (direction === "right") handleLike();
    else handlePass();
  };

  if (isChecking || isLoading) {
    return (
      <>
        <AppHeader />
        <main className="min-h-screen bg-brand-background flex items-center justify-center pt-16">
          <Loader2 className="w-12 h-12 animate-spin text-brand-primary" />
        </main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AppHeader />
        <main className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-16">
          <div className="text-center space-y-4 max-w-md">
            <p className="text-xl font-semibold text-white">
              Oops! Something went wrong
            </p>
            <p className="text-gray-400">
              {error instanceof Error ? error.message : "Failed to load matches"}
            </p>
          </div>
        </main>
      </>
    );
  }

  const matches = data?.matches || [];
  const currentMatch = matches[currentIndex];

  if (!currentMatch) {
    return (
      <>
        <AppHeader />
        <main className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-16">
          <div className="text-center space-y-6 max-w-md">
            <div className="w-24 h-24 mx-auto rounded-full bg-brand-surface border border-brand-border flex items-center justify-center shadow-xl">
              <Heart className="w-12 h-12 text-brand-primary" />
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-white">
                You're all caught up!
              </h1>
              <p className="text-lg text-gray-400">
                Check back soon for new matches
              </p>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <AppHeader />
      <main className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-16 overflow-hidden relative">
        <div className="absolute inset-0 bg-brand-radial pointer-events-none" />
        <MatchCard match={currentMatch} onSwipe={handleSwipe} />
      </main>

      {/* AI Chat Panel - context-aware with current profile */}
      <AIChatPanel
        contextProfile={{
          id: currentMatch.profileId, // Use profileId (not userId) for DB foreign key
          displayName: currentMatch.profile.displayName,
          age: currentMatch.profile.age,
          bio: currentMatch.profile.bio,
          lookingFor: currentMatch.profile.lookingFor,
          photos: currentMatch.profile.photos,
        }}
        analyzePhotos
      />

      {matchedUser && (
        <MatchModal
          match={matchedUser}
          onClose={() => setMatchedUser(null)}
        />
      )}
    </>
  );
}
